import Phaser from 'phaser';
import { sfx } from '../audio/sfx';
import { TILE_COLORS } from '../core/types';
import { preloadLoaderAssets } from '../ui/glenPaint';
import { enterGlen, warmAfterTitle } from './warmup';
import { driftPetals, spawnFireflies } from '../fx/ambience';
import { tileKey, TEX } from '../ui/assets';
import { TITLE_CAST } from '../ui/cast';
import { addPortrait } from '../ui/actors';
import { stage } from '../ui/stage';
import { SettingsSheet } from '../ui/settingsSheet';
import { addButton, addSoundButton, addText } from '../ui/widgets';
import { addWoodPanel, woodType } from '../ui/woodPanel';
import { showWings } from '../ui/wings';

export class TitleScene extends Phaser.Scene {
  private newerShown = false;

  constructor() {
    super('title');
  }

  private showNewerSave(): void {
    if (this.newerShown) return;
    this.newerShown = true;
    const wide = stage.mode === 'wide';
    const y = wide ? 760 : 920;
    addWoodPanel(this, stage.w / 2 - 320, y - 32, 640, 64, 12);
    addText(this, stage.w / 2, y, 'This glen was saved by a newer version.', woodType('24px'))
      .setOrigin(0.5)
      .setDepth(13);
  }

  create(): void {
    void preloadLoaderAssets();
    sfx.enterScene('title');
    const wide = stage.mode === 'wide';
    const cx = stage.w / 2;
    this.add.image(cx, stage.h / 2, TEX.bgTitle).setDisplaySize(stage.w, stage.h).setDepth(0);
    spawnFireflies(this, { x: 24, y: 36, w: stage.w - 48, h: wide ? 160 : 220 }, wide ? 10 : 8);
    driftPetals(this, wide ? 8 : 6, { x: 20, y: 80, w: stage.w - 40, h: wide ? 280 : 420 });

    const logoY = wide ? 200 : 292;
    const logo = this.add.image(cx, logoY, TEX.logo).setDisplaySize(wide ? 460 : 460, wide ? 300 : 331).setDepth(6);
    this.tweens.add({
      targets: logo,
      y: logoY - 14,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const studioY = wide ? 390 : 500;
    addWoodPanel(this, cx - 240, studioY - 28, 480, 56, 5);
    addText(this, cx, studioY, 'Shenandoah Game Studios', woodType('24px'))
      .setOrigin(0.5)
      .setDepth(6);

    const tileY = wide ? 500 : 620;
    const tileSpan = wide ? 120 : 104;
    const tileStart = cx - ((TILE_COLORS.length - 1) * tileSpan) / 2;
    TILE_COLORS.forEach((color, index) => {
      const x = tileStart + index * tileSpan;
      const y = tileY;
      const img = this.add.image(x, y, tileKey(color)).setDisplaySize(84, 84).setDepth(6);
      this.tweens.add({
        targets: img,
        y: y - 12,
        duration: 1200 + index * 140,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    addButton(this, cx, wide ? 680 : 820, 420, 'Enter the glen', () => {
      void enterGlen(this).then((opened) => {
        if (!opened && this.scene.isActive()) this.showNewerSave();
      });
    }, { fontSize: '34px', depth: 8 });

    const heroY = wide ? 820 : 1040;
    const heroSpan = wide ? 200 : 160;
    const heroStart = cx - ((TITLE_CAST.length - 1) * heroSpan) / 2;
    TITLE_CAST.forEach((id, index) => {
      const x = heroStart + index * heroSpan;
      const y = heroY;
      const portrait = addPortrait(this, x, y, id, 132, 6);
      this.tweens.add({
        targets: [portrait.face, portrait.frame],
        y: y - 8,
        duration: 1500 + index * 160,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    const settings = new SettingsSheet(this);
    const shot = new URLSearchParams(globalThis.location?.search ?? '').get('shot') ?? '';
    if (shot === 'combo' || shot === 'sigs' || shot === 'rival' || shot === 'venue' || shot === 'spark' || shot === 'snail-cross' || shot === 'charms' || shot.startsWith('sig-')) {
      this.time.delayedCall(500, () => {
        if (this.scene.isActive()) void enterGlen(this);
      });
    }
    addButton(this, wide ? 180 : 150, wide ? 72 : 78, 220, 'Settings', () => settings.open(), {
      skin: 'berry',
      fontSize: '28px',
      depth: 30,
    });
    addSoundButton(this, wide ? stage.w - 80 : 640, wide ? 72 : 72, 30);
    showWings(
      {
        heroId: 'pip',
        kicker: 'Match three',
        title: 'Swap a neighbor',
        lines: ['Three of a kind clear.', 'Four makes a special.'],
      },
      {
        heroId: 'cleo',
        kicker: 'The glen',
        title: 'Highest score wins',
        lines: ['Four rounds.', 'Five moves each.'],
      },
    );
    requestAnimationFrame(() => {
      if (this.scene.isActive()) warmAfterTitle(this);
    });
  }
}
